import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import {createUUId} from "../../../../core/services/utils/utils";
import Sizes from "../../../../core/models/static/sizes";
import ElementIds from "../../../../core/models/element-ids";
import useIsMounted from "../../../hooks/use-is-mounted";
import {usePartnerInformation} from "../../../hooks/use-partner-information";
import Utils from "../../../../core/services/utils/utils";
import {
    buildMockPurchaseHistory,
    buildMockPurchaseOrder,
} from "../../panel/common/purchases/shared/mock-documents";
import PrintPurchaseTemplate from "./template";
import PrintPurchaseViewPage from "./page";
import PrintPurchaseHeaderToolbar from "./header";
import usePrintPreviewScale from "../../../hooks/use-print-preview-scale";

export const PrintPurchaseViewTableTypes = {
    products: 'products',
};

/**
 * Fetches the elements that are needed for the computation of the print pages.
 */
export const getElements = () => ({
    page: document.getElementById(ElementIds.printPurchaseView.page),
    table: document.getElementById(ElementIds.printPurchaseView.table),
    pageHeader: document.getElementById(ElementIds.printPurchaseView.pageHeader),
    productsTable: {
        header: document.getElementById(ElementIds.printPurchaseView.productsTable.header),
        body: document.getElementById(ElementIds.printPurchaseView.productsTable.body),
        bodyFiller: document.getElementById(ElementIds.printPurchaseView.productsTable.bodyFiller),
        footerContinue: document.getElementById(ElementIds.printPurchaseView.productsTable.footerContinue),
        footer: document.getElementById(ElementIds.printPurchaseView.productsTable.footer),
    },
});

/**
 * Fetches the static heights of the template elements.
 * @param {any} elements
 */
export const getHeights = (elements) => ({
    productsTableStaticHeight: elements.productsTable.header.scrollHeight + elements.productsTable.footer.scrollHeight,
    productsTableStaticHeightWithContinue: elements.productsTable.header.scrollHeight + elements.productsTable.footerContinue.scrollHeight,
    productsTableFooterStaticHeight: elements.productsTable.footer.scrollHeight,
    productsTableFooterStaticHeightWithContinue: elements.productsTable.footerContinue.scrollHeight,
});

/**
 * @typedef {{
 *     pageNumber: number,
 *     totalPages: number,
 *     entities: {name: string, range: [number, number], useContinueFooter: boolean, spacerHeight: number}[],
 *     key: string,
 * }} PrintPurchasePage
 */

/**
 * Purchase order / history print view (products-only pagination).
 * @param {{history?: boolean}} props
 */
const PrintPurchaseView = ({history = false}) => {
    const params = useParams();
    const [printInfoReady, setPrintInfoReady] = useState(false);
    const [documentData, setDocumentData] = useState(null);
    const [pages, setPages] = useState([]);
    const isMounted = useIsMounted();

    const partnerInformation = usePartnerInformation();
    const {rootRef: printPreviewRootRef, style: printPreviewScaleStyle} = usePrintPreviewScale({
        pageElementId: ElementIds.printPurchaseView.page,
        ready: !!documentData && !!partnerInformation,
    });
    const partner = useMemo(() => !partnerInformation ? partnerInformation : ({
        ...partnerInformation,
        logo: (partnerInformation?.configuration?.PartnerLogoUrlBaseAddress ?? '') + (partnerInformation?.logo ?? ''),
    }), [partnerInformation]);
    const partnerInfoReady = !!partner;

    const ready = useMemo(() => printInfoReady && partnerInfoReady, [printInfoReady, partnerInfoReady]);

    /**
     * Loads purchase document shell data.
     * // TODO: replace with live GetPurchaseOrder / GetPurchaseHistory APIs
     */
    const getDocumentInformation = useCallback(async () => {
        setPrintInfoReady(false);
        await Utils.wait(200);
        if (!isMounted()) {
            return;
        }
        if (history) {
            setDocumentData(buildMockPurchaseHistory({
                historyId: params.historyId,
                vendorId: params.vendorId,
            }));
        } else {
            setDocumentData(buildMockPurchaseOrder({
                orderId: params.orderId,
                vendorId: params.vendorId,
            }));
        }
    }, [history, isMounted, params.historyId, params.orderId, params.vendorId]);

    useEffect(() => {
        void getDocumentInformation();
    }, [getDocumentInformation]);

    /**
     * Creates the pages that are to be rendered for printing.
     * @type {(function(elements: any, heights: any): PrintPurchasePage[])}
     */
    const createPages = useCallback((elements, heights) => {
        const MAX_PAGE_HEIGHT = elements.page.clientHeight
            - (Sizes.defaultPagePadding * 2)
            - elements.pageHeader.scrollHeight;
        /** @type {HTMLCollection} */
        const products = elements.productsTable.body.children;

        let productsIndex = 0;
        let pagesAccumulator = [];
        let productsNormalFooterAdded = false;

        while (productsIndex < products.length) {
            const currentPageProducts = [];
            let remainingHeight = MAX_PAGE_HEIGHT;
            let useProductsContinueFooter = false;

            while (productsIndex < products.length) {
                const nextProductHeight = products[productsIndex].scrollHeight;

                if ((remainingHeight - nextProductHeight) < heights.productsTableStaticHeight) {
                    useProductsContinueFooter = true;
                    break;
                }

                currentPageProducts.push(productsIndex);
                remainingHeight -= nextProductHeight;
                productsIndex++;
            }

            if (useProductsContinueFooter) {
                while (productsIndex < products.length) {
                    const nextProductHeight = products[productsIndex].scrollHeight;

                    if ((remainingHeight - nextProductHeight) < heights.productsTableStaticHeightWithContinue) {
                        break;
                    }

                    currentPageProducts.push(productsIndex);
                    remainingHeight -= nextProductHeight;
                    productsIndex++;
                }
            }

            if (!productsNormalFooterAdded && !useProductsContinueFooter) {
                productsNormalFooterAdded = true;
            }

            if (useProductsContinueFooter) {
                remainingHeight -= heights.productsTableFooterStaticHeightWithContinue;
            }
            if (!useProductsContinueFooter && productsNormalFooterAdded) {
                remainingHeight -= heights.productsTableFooterStaticHeight;
            }

            pagesAccumulator.push({
                products: currentPageProducts,
                remainingHeight,
                useProductsContinueFooter,
            });
        }

        if (!productsNormalFooterAdded) {
            pagesAccumulator.push({
                products: [],
                remainingHeight: MAX_PAGE_HEIGHT - heights.productsTableFooterStaticHeight,
                useProductsContinueFooter: false,
            });
        }

        for (const page of pagesAccumulator) {
            if (page.products.length === 0 && products.length > 0) {
                page.products.push(products.length - 1);
            }
        }

        return pagesAccumulator.map((page, index, array) => {
            const productsEntity = {
                name: PrintPurchaseViewTableTypes.products,
                range: [
                    page.products.reduce((min, p) => (p < min ? p : min), Number.MAX_SAFE_INTEGER),
                    page.products.reduce((max, p) => (p > max ? p : max), Number.MIN_SAFE_INTEGER),
                ].filter((e) => e !== Number.MIN_SAFE_INTEGER && e !== Number.MAX_SAFE_INTEGER),
                useContinueFooter: page.useProductsContinueFooter,
                spacerHeight: page.remainingHeight,
            };
            const entities = [];
            if (productsEntity.range.length === 2 || !productsEntity.useContinueFooter) {
                entities.push(productsEntity);
            }
            return {
                entities,
                key: createUUId(),
                pageNumber: index + 1,
                totalPages: array.length,
            };
        });
    }, []);

    /**
     * Sets the pages that are to be rendered for printing.
     */
    const setPrintPages = useCallback(() => {
        const elements = getElements();
        const heights = getHeights(elements);
        setPages(createPages(elements, heights));
    }, [createPages]);

    /**
     * After document + partner are ready, measure the hidden template and build pages.
     */
    useEffect(() => {
        if (!documentData || !partner) {
            setPrintInfoReady(false);
            return;
        }
        setPrintInfoReady(false);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setPrintPages();
                setPrintInfoReady(true);
            });
        });
    }, [documentData, partner, setPrintPages]);

    return (
        <div
            ref={printPreviewRootRef}
            className={'print-invoice-view'}
            style={printPreviewScaleStyle}
            data-mui-color-scheme="light"
        >
            {
                !!documentData &&
                <PrintPurchaseHeaderToolbar/>
            }
            {
                !ready &&
                <div className={'min-vh-100 d-flex justify-content-center align-items-center'}>
                    <div className={'loading print-invoice-page'}>
                        <div/>
                    </div>
                </div>
            }
            {
                ready && !!documentData &&
                <div className={'pages'}>
                    {
                        pages.map((page) => (
                            <PrintPurchaseViewPage
                                key={page.key}
                                page={page}
                            />
                        ))
                    }
                </div>
            }
            {
                !!documentData && !!partner &&
                <PrintPurchaseTemplate
                    history={history}
                    document={documentData}
                    partner={partner}
                />
            }
        </div>
    );
};

export default PrintPurchaseView;
